import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import api from "../../lib/api";
import {
  Inbox, Briefcase, BookOpen, MapPin, Layers,
  Sparkles, Users, Settings as SettingsIcon, LogOut, ExternalLink, MessageSquareQuote,
} from "lucide-react";

const NAV = [
  { to: "/admin/leads", label: "Leads", icon: Inbox },
  { to: "/admin/generations", label: "AI Designs", icon: Sparkles },
  { to: "/admin/projects", label: "Projects", icon: Briefcase },
  { to: "/admin/blog", label: "Blog", icon: BookOpen },
  { to: "/admin/testimonials", label: "Reviews", icon: MessageSquareQuote },
  { to: "/admin/areas", label: "Service Areas", icon: MapPin },
  { to: "/admin/services", label: "Services", icon: Layers },
  { to: "/admin/founders", label: "Founders", icon: Users },
  { to: "/admin/settings", label: "Settings", icon: SettingsIcon },
];

export default function AdminLayout() {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get("/auth/me").catch(() => nav("/admin/login"));
  }, [nav]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    nav("/admin/login");
  };

  return (
    <div data-testid="admin-layout" className="min-h-screen bg-[#0d0d0d] text-[#F5F3EF] flex">
      {/* Sidebar */}
      <aside
        className={`${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 fixed lg:sticky top-0 left-0 z-40 w-64 h-screen bg-[#151515] border-r border-[#D8D6D0]/10 transition-transform duration-300 flex flex-col`}
      >
        <div className="px-6 py-7 border-b border-[#D8D6D0]/10">
          <Link to="/admin" data-testid="admin-brand" className="flex items-center gap-3">
            <img src="/logo.png" alt="NorthCrest" className="w-12 h-12 object-contain" />
            <div className="leading-tight">
              <div className="font-serif-display text-xl">NorthCrest</div>
              <div className="overline text-[10px] text-[#D8D6D0]/50">CMS</div>
            </div>
          </Link>
        </div>

        <nav className="flex-1 overflow-y-auto py-4">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-6 py-3 text-sm transition-colors ${
                  isActive
                    ? "bg-[#0B2B1F] text-[#F5F3EF] border-l-2 border-[#8C1D30]"
                    : "text-[#D8D6D0]/65 hover:bg-[#1f1f1f] hover:text-[#F5F3EF] border-l-2 border-transparent"
                }`
              }
              onClick={() => setOpen(false)}
            >
              <n.icon className="w-4 h-4" />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="px-4 py-4 border-t border-[#D8D6D0]/10 space-y-2">
          <a
            href="/"
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-3 px-3 py-2.5 text-xs uppercase tracking-widest text-[#D8D6D0]/60 hover:text-[#F5F3EF] transition-colors"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            View Site
          </a>
          <button
            data-testid="admin-logout"
            onClick={logout}
            className="flex items-center gap-3 px-3 py-2.5 w-full text-left text-xs uppercase tracking-widest text-[#D8D6D0]/60 hover:text-[#F5F3EF] transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {open && (
        <button
          aria-label="Close menu"
          onClick={() => setOpen(false)}
          className="lg:hidden fixed inset-0 z-30 bg-black/60"
        />
      )}

      {/* Main */}
      <div className="flex-1 min-w-0">
        <header className="lg:hidden sticky top-0 z-20 bg-[#151515] border-b border-[#D8D6D0]/10 px-4 py-3 flex items-center justify-between">
          <button
            data-testid="admin-mobile-menu"
            onClick={() => setOpen(true)}
            className="px-3 py-2 border border-[#D8D6D0]/15 text-xs uppercase tracking-widest"
          >
            Menu
          </button>
          <div className="font-serif-display text-lg">NorthCrest CMS</div>
        </header>

        <main className="px-6 md:px-10 py-10 max-w-7xl">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
